/**
 * Schema.org JSON-LD payloads for the marketing site.
 *
 * Site-wide schemas (Organization, WebSite, SoftwareApplication) are injected
 * once on Landing. Page-specific schemas (FAQPage) are built per-page from the
 * same FAQ arrays the components already render — single source of truth.
 *
 * Validate after deploy at:
 *   https://search.google.com/test/rich-results
 *   https://validator.schema.org/
 */

const SITE_URL = "https://pipeguide.tech";
const APP_URL = "https://app.pipeguide.tech";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_deal-readiness/artifacts/wjetx0cs_Pipeguide%20Logo%204.PNG";

// ----- Organization -----
export const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "PipeGuide",
    legalName: "PipeGuide",
    url: SITE_URL,
    logo: {
        "@type": "ImageObject",
        url: LOGO_URL,
        width: 1024,
        height: 1024,
    },
    description:
        "PipeGuide is the real-time sales qualification assistant. It scores deal readiness against BANT, MEDDIC or SPIN as the call happens and surfaces AI-suggested next questions for sales teams worldwide.",
    contactPoint: [
        {
            "@type": "ContactPoint",
            contactType: "customer support",
            email: "support@pipeguide.tech",
            availableLanguage: ["English"],
        },
        {
            "@type": "ContactPoint",
            contactType: "sales",
            email: "support@pipeguide.tech",
            availableLanguage: ["English"],
        },
    ],
    // Add real social profiles to `sameAs` as they go live.
    // sameAs: ["https://www.linkedin.com/company/pipeguide", "https://x.com/pipeguidetech"],
};

// ----- WebSite (with sitelinks search box potential) -----
export const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: "PipeGuide",
    description: "The real-time sales qualification assistant for sales teams worldwide.",
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: "en",
};

// ----- SoftwareApplication -----
export const softwareApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": `${SITE_URL}/#software`,
    name: "PipeGuide",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Sales Intelligence",
    operatingSystem: "Web",
    url: APP_URL,
    description:
        "PipeGuide scores discovery and qualification calls live against BANT, MEDDIC or SPIN and tells reps the next best question to ask. Built for sales teams worldwide.",
    image: LOGO_URL,
    publisher: { "@id": `${SITE_URL}/#organization` },
    featureList: [
        "Real-time qualification scoring against BANT, MEDDIC or SPIN",
        "AI-suggested questions every 15 seconds during the call",
        "Custom qualification frameworks with weighted categories",
        "Meeting Bot for Zoom, Teams and Google Meet",
        "Browser microphone capture (no install)",
        "PDF export of call analysis",
        "CRM integrations (HubSpot, Pipedrive, Zoho)",
        "Google SSO and JWT authentication",
        "Multi-tenant org isolation",
        "Manager analytics, leaderboard and AI coaching insights",
    ],
    offers: [
        {
            "@type": "Offer",
            name: "Starter",
            price: "0",
            priceCurrency: "ZAR",
            availability: "https://schema.org/InStock",
            description: "60-day free trial — 1 rep, 5 calls/month, 20-minute Meeting Bot cap.",
            url: `${SITE_URL}/pricing`,
        },
        {
            "@type": "Offer",
            name: "Pro",
            price: "1299",
            priceCurrency: "ZAR",
            availability: "https://schema.org/InStock",
            description: "3 rep seats, 12 calls per rep/month, unlimited Meeting Bot, PDF export, basic analytics. Month-to-month, cancel anytime.",
            url: `${SITE_URL}/pricing`,
            priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "1299",
                priceCurrency: "ZAR",
                referenceQuantity: { "@type": "QuantitativeValue", value: "1", unitCode: "MON" },
                billingDuration: "P1M",
            },
        },
        {
            "@type": "Offer",
            name: "Business",
            price: "4499",
            priceCurrency: "ZAR",
            availability: "https://schema.org/InStock",
            description: "10 rep seats, unlimited managers, 15 calls per rep/month, full analytics + leaderboard, custom branding. Month-to-month, cancel anytime.",
            url: `${SITE_URL}/pricing`,
            priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: "4499",
                priceCurrency: "ZAR",
                referenceQuantity: { "@type": "QuantitativeValue", value: "1", unitCode: "MON" },
                billingDuration: "P1M",
            },
        },
    ],
    aggregateRating: undefined, // Add when real customer reviews are collected.
};

/**
 * Build a FAQPage schema payload from the same {q, a} array the page renders.
 * @param {Array<{q:string,a:string}>} faqs
 * @returns {object}
 */
export function buildFaqSchema(faqs) {
    if (!Array.isArray(faqs) || faqs.length === 0) return null;
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: {
                "@type": "Answer",
                text: f.a,
            },
        })),
    };
}
